<template>
  <header class="cg-wizard-toolbar">
    <div v-if="store.gommista" class="cg-wizard-gommista">
      <img
        v-if="logoUrl"
        class="cg-wizard-logo"
        :src="logoUrl"
        :alt="store.gommista.denominazione || store.gommista.ragione_sociale"
      />
      <div class="cg-wizard-info">
        <h1 class="cg-wizard-name">{{ store.gommista.denominazione || store.gommista.ragione_sociale }}</h1>
        <p v-if="indirizzo" class="cg-wizard-address">{{ indirizzo }}</p>
        <a
          v-if="store.gommista.cellulare"
          :href="'tel:' + store.gommista.cellulare"
          class="cg-wizard-tel"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="18" height="18" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          {{ store.gommista.cellulare }}
        </a>
        <a
          v-if="store.gommista.email"
          :href="'mailto:' + store.gommista.email"
          class="cg-wizard-tel cg-wizard-email"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="18" height="18" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          {{ store.gommista.email }}
        </a>
      </div>
    </div>
    <section v-if="mostraTariffe" class="cg-wizard-tariffe">
      <h3 class="cg-wizard-tariffe-title">Tariffe</h3>
      <div class="cg-wizard-tariffe-list">
        <div class="cg-wizard-tariffa">
          <span class="cg-wizard-tariffa-label">Costo cambio gomme auto</span>
          <span class="cg-wizard-tariffa-valore">€ {{ store.gommista.prezzo_montaggio_smontaggio_auto }}</span>
        </div>
        <div class="cg-wizard-tariffa">
          <span class="cg-wizard-tariffa-label">Deposito gomme auto</span>
          <span class="cg-wizard-tariffa-valore">€ {{ store.gommista.prezzo_deposito_auto }}</span>
        </div>
        <div class="cg-wizard-tariffa">
          <span class="cg-wizard-tariffa-label">Costo cambio gomme moto</span>
          <span class="cg-wizard-tariffa-valore">€ {{ store.gommista.prezzo_montaggio_smontaggio_moto }}</span>
        </div>
        <div class="cg-wizard-tariffa">
          <span class="cg-wizard-tariffa-label">Deposito gomme moto</span>
          <span class="cg-wizard-tariffa-valore">€ {{ store.gommista.prezzo_deposito_moto }}</span>
        </div>
      </div>
    </section>
    <nav class="cg-wizard-steps" aria-label="Passaggi prenotazione">
      <span
        class="cg-wizard-step"
        :class="{ 'cg-wizard-step--current': store.step === 'slot', 'cg-wizard-step--done': stepOrder(store.step) > 1 }"
      >
        <span class="cg-wizard-step-num">1</span>
        <span class="cg-wizard-step-label">Data e ora</span>
      </span>
      <span class="cg-wizard-step-sep" aria-hidden="true"></span>
      <span
        class="cg-wizard-step"
        :class="{ 'cg-wizard-step--current': store.step === 'form', 'cg-wizard-step--done': stepOrder(store.step) > 2 }"
      >
        <span class="cg-wizard-step-num">2</span>
        <span class="cg-wizard-step-label">I tuoi dati</span>
      </span>
      <span class="cg-wizard-step-sep" aria-hidden="true"></span>
      <span
        class="cg-wizard-step"
        :class="{ 'cg-wizard-step--current': store.step === 'landing', 'cg-wizard-step--done': store.step === 'landing' }"
      >
        <span class="cg-wizard-step-num">3</span>
        <span class="cg-wizard-step-label">Conferma</span>
      </span>
    </nav>
  </header>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  store: { type: Object, required: true },
  /** Base URL per immagini (es. API Django). Se non passato, il logo non viene mostrato. */
  staticBaseUrl: { type: String, default: '' },
})

const logoUrl = computed(() => {
  const g = props.store.gommista
  if (!g?.logo || !props.staticBaseUrl) return ''
  const base = props.staticBaseUrl.replace(/\/$/, '')
  return base + (g.logo.startsWith('/') ? g.logo : '/' + g.logo)
})

const indirizzo = computed(() => {
  const g = props.store.gommista
  if (!g) return ''
  const parts = [g.indirizzo, g.citta, g.provincia].filter(Boolean)
  return parts.join(' – ')
})

const mostraTariffe = computed(() => {
  const g = props.store.gommista
  return g && (g.prezzo_esposto === 'S' || g.prezzo_esposto === true)
})

function stepOrder(step) {
  const o = { slot: 1, form: 2, landing: 3 }
  return o[step] || 0
}
</script>

<style scoped>
.cg-wizard-toolbar {
  font-family: system-ui, -apple-system, sans-serif;
  padding: 1rem 0;
  margin-bottom: 1rem;
  border-bottom: 1px solid #d1d5db;
}

.cg-wizard-gommista {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}

.cg-wizard-logo {
  width: 80px;
  height: 80px;
  object-fit: contain;
  flex-shrink: 0;
  background: #f3f4f6;
  border-radius: 4px;
}

.cg-wizard-info {
  flex: 1;
  min-width: 0;
}

.cg-wizard-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.25rem 0;
  line-height: 1.3;
}

.cg-wizard-address {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 0.25rem 0;
}

.cg-wizard-tel {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.875rem;
  color: #015EA9;
  text-decoration: none;
  margin-top: 0.25rem;
}

.cg-wizard-tel:hover {
  text-decoration: underline;
}

.cg-wizard-email {
  display: block;
  margin-top: 0.25rem;
}

.cg-wizard-tariffe {
  margin-bottom: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e5e7eb;
}

.cg-wizard-tariffe-title {
  font-size: 1rem;
  font-weight: 700;
  color: #000;
  margin: 0 0 0.5rem 0;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid #000;
}

.cg-wizard-tariffe-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.cg-wizard-tariffa {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
}

.cg-wizard-tariffa-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #ca8a04;
  text-transform: uppercase;
}

.cg-wizard-tariffa-valore {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
}

.cg-wizard-steps {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.cg-wizard-step {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8125rem;
  color: #9ca3af;
}

.cg-wizard-step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: #e5e7eb;
  color: #6b7280;
  font-weight: 600;
}

.cg-wizard-step--current .cg-wizard-step-num {
  background: #ca8a04;
  color: #fff;
}

.cg-wizard-step--current .cg-wizard-step-label {
  color: #111827;
  font-weight: 600;
}

.cg-wizard-step--done .cg-wizard-step-num {
  background: #10b981;
  color: #fff;
}

.cg-wizard-step-sep {
  width: 1rem;
  height: 1px;
  background: #d1d5db;
  margin: 0 0.1rem;
}
</style>
